<template>
	<div
		v-click-outside="inputBlurHandler"
		:class="$style['inline']"
	>
		<div
			:class="[$style['base-multi-select-container'], {
				[$style['base-multi-select-container--multiple']]: multiple
			}]"
			@click="handleChipClick"
		>
			<div
				:class="[$style['base-multi-select'], {
					[$style['base-multi-select--editable']]: editable && !isFocused,
					[$style['base-multi-select--focused']]: isFocused
				}]"
			>
				<div
					v-for="(chip, index) in chips"
					:key="index"
					:class="$style['chip']"
					:data-index="index"
				>
					{{ chip }}
					<FontAwesomeIcon
						v-if="editableFocus"
						:class="$style['chip__button']"
						:icon="['fas', 'times-circle']"
						size="lg"
						@click.stop="removeChip(chip)"
					/>
				</div>
				<span
					v-if="editable && chips.length === 0 && !isFocused"
					style="padding-left: 10px; z-index:2;"
				>
					-
				</span>
				<input
					v-show="editableFocus"
					ref="input"
					v-model="input"
					class="input"
					:class="[$style['base-multi-select__input']]"
					type="text"
					@focus="inputFocusHandler"
					@keydown.prevent.enter="addChip"
					@keydown.delete="removeBack"
				/>
				<FloatingLabel
					ref="label"
					:class="$style['base-multi-select__label']"
					:label-text="labelText"
					:is-focused="isFocused"
					:is-floated="isFloated"
				/>
				<NotchedOutline
					v-show="editableFocus"
					:class="$style['base-multi-select__outline']"
					:is-focused="isFocused"
					:is-floated="isFloated && !!labelText"
				/>
				<NotchedOutlineIdle
					v-show="!editable || isFocused"
					:class="$style['base-multi-select__outline']"
					:is-focused="isFocused"
					:is-floated="isFloated && !!labelText"
				/>
				<FontAwesomeIcon
					v-if="editable && !isFocused"
					:icon="['fal', 'pencil']"
					:class="$style['base-multi-select__edit']"
				/>
			</div>
		</div>
		<BaseIconButton
			v-if="showButton"
			:class="$style['span-button']"
			@click="addChip"
		>
			<FontAwesomeIcon :icon="['fal', 'plus']" />
		</BaseIconButton>
	</div>
</template>

<script>
import NotchedOutline from '@c:essentials/NotchedOutline'
import NotchedOutlineIdle from '@c:essentials/NotchedOutlineIdle'
import FloatingLabel from '@c:essentials/FloatingLabel'
export default {
	name: 'BaseMultiSelect',

	components: {
		NotchedOutline,
		NotchedOutlineIdle,
		FloatingLabel
	},

	model: {
		prop: 'model',
		event: 'input'
	},

	props: {
		maxItems: {
			type: [String, Number],
			default: 0
		},
		saveOnBlur: {
			type: [String, Boolean],
			default: false
		},
		showButton: {
			type: [String, Boolean],
			default: false
		},
		model: {
			type: Array,
			default: () => []
		},
		labelText: {
			type: String,
			default: null
		},
		multiple: {
			type: [Boolean, String],
			default: false
		},
		editable: {
			type: Boolean,
			default: false
		}
	},

	data: () => ({
		isFocused: false,
		isFloated: false,
		input: null,
		chipsLocal: []
	}),

	computed: {
		chips: {
			get () {
				return this.chipsLocal
			},
			set (updated) {
				this.chipsLocal = updated
			}
		},
		editableFocus () {
			if (this.editable) {
				return this.isFocused
			}
			return true
		}
	},

	watch: {
		model: {
			handler () {
				if (this.model.length > 0) {
					this.chips = this.model
				} else {
					this.chips = []
				}
				if (this.chips.length > 0) {
					this.isFloated = true
				}
			}
		}
	},

	created () {
		if (this.editable) {
			this.isFloated = true
		}
		if (this.model.length > 0) {
			this.chips = this.model
		}
		if (this.chips.length > 0) {
			this.isFloated = true
		}
	},

	methods: {

		addChip () {
			const input = this.input && this.input.trim()
			if (this.chips.indexOf(input) === -1 && input !== '' && input !== null) {
				// if (input !== '' && input !== null) {

				if ( this.maxItems && this.chips.length >= this.maxItems) {
					this.chips.shift()
				}

				this.chips.push(input)
				this.input = null
				this.$emit('input', this.chipsLocal)
			}
		},

		removeChip (chipToRemove) {
			this.chips = this.chips.filter(chip => chip !== chipToRemove)
			if (this.chipsLocal.length === 0 && !this.isFocused) {
				this.isFloated = false
			}
			this.$emit('input', this.chipsLocal)
		},

		removeBack () {
			if (this.chips.length) {
				if (this.input === '' || this.input === null) {
					this.input = this.chips.pop()
					this.$emit('input', this.chipsLocal)
				}
			}
		},

		inputFocusHandler (event) {
			this.isFocused = true
			this.isFloated = true
			this.$emit('focus', event)
		},

		inputBlurHandler (_event) {
			if (!this.isFocused) return
			if (this.saveOnBlur) {
				this.addChip()
			}
			this.input = null
			this.isFocused = false
			if (!this.chips.length && !this.editable) {
				this.isFloated = false
			}
			this.$emit('blur', this.chipsLocal)
		},

		handleChipClick () {
			if (this.editable && !this.isFocused) {
				this.inputFocusHandler()
				this.$nextTick(() => {
					this.$refs.input.focus()
				})
			}

		}
	}
}
</script>
<style lang="scss" module>
    .span-button {
        margin-left: $gutter;
    }

    .inline {
        display: flex;
        align-items: center;
    }

    .chip {
        background-color: getColor(chip);
        color: getColor(text);
        display: inline-flex;
        padding: 7px 12px;
        border: 1px solid;
        border-color: getColor(border);
        border-radius: 5px;
        align-items: center;
        cursor: default;
        min-height: 30px;
        height: 1px;
        margin: 2.5px;
        font-size: 14px;
        z-index: 2;

        &__button {
            z-index: 2;
            margin-top: 2px;
            width: 12px;
            height: 15px;
            font-size: 12px;
            color: getColor(grey);
            margin-left: 8px;
            margin-right: 0;

            &:hover {
                opacity: 0.7;
                cursor: pointer;
            }
        }
    }

    .base-multi-select-container {
        position: relative;
        width: 100%;

        &--multiple {
            padding: 0 15px;
        }
    }

    .base-multi-select {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        position: relative;
        min-height: 48px;
        margin: $gutter * 1.25 0;
        padding: 10px;

        // --- NEW CODE START ---
        // Adds the hover effect to match BaseTextField
        &:not(.base-multi-select--focused):hover {
            .base-multi-select__outline {
                border-color: getColor(text);
            }
        }
        // --- NEW CODE END ---

        &--with-dropdown {
            .base-multi-select__input {
                padding-right: 25px !important;
            }
        }

        &--editable {
            &:hover {
                cursor: pointer;

                &::before {
                    z-index: 1;
                    content: "";
                    background-color: getColor(mark);
                    inset: 5px 10px;
                    position: absolute;
                }

                .base-multi-select__edit {
                    opacity: 1;
                }
            }
        }

        &__outline {
            background-color: getColor(input-background);
        }

        &__label {
            z-index: 1;
        }

        &__input {
            z-index: 2;
            min-width: 100px;
            flex: 1;
            margin-left: 10px;
            height: 100%;
            outline: 0;
            color: getColor(text);
            font-size: 16px;
            background-color: transparent;
            border: 0;
            width: 100%;
        }

        &__edit {
            position: absolute;
            z-index: 2;
            opacity: 0;
            right: 15px;
            font-size: 18px;
        }
    }
</style>
